import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, Skeleton } from '@org/ui';
import { useTranslation } from 'react-i18next';
import { Table2, BarChart3 } from 'lucide-react';

type ReportCardProps = {
  title: string;
  description?: string;
  isLoading?: boolean;
  error?: unknown;
  /** Rendered instead of the children when they would be empty. */
  isEmpty?: boolean;
  /** A table twin of the chart; adds a chart/table toggle. */
  table?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
};

/**
 * The frame every report sits in: title + one-line explanation, consistent
 * loading/error/empty states, and an optional chart/table switch so no
 * number is reachable only through a hover tooltip.
 */
export function ReportCard({
  title,
  description,
  isLoading,
  error,
  isEmpty,
  table,
  actions,
  className,
  children,
}: ReportCardProps) {
  const { t } = useTranslation('admin');
  const [showTable, setShowTable] = React.useState(false);

  let body: React.ReactNode;
  if (isLoading) {
    body = (
      <div className="space-y-3 py-2">
        <Skeleton className="h-6 w-1/3" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  } else if (error) {
    body = <p className="py-8 text-center text-sm text-destructive">{t('analytics.common.error')}</p>;
  } else if (isEmpty) {
    body = <p className="py-8 text-center text-sm text-muted-foreground">{t('analytics.common.noData')}</p>;
  } else {
    body = showTable && table ? table : children;
  }

  return (
    <Card className={className}>
      <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
        <div className="min-w-0">
          <CardTitle className="text-base font-semibold leading-tight">{title}</CardTitle>
          {description ? <CardDescription className="mt-1 text-xs">{description}</CardDescription> : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {actions}
          {table && !isLoading && !error && !isEmpty ? (
            <button
              type="button"
              onClick={() => setShowTable((v) => !v)}
              className="inline-flex h-8 items-center gap-1 rounded-md border px-2 text-xs text-muted-foreground hover:bg-muted"
              aria-pressed={showTable}
            >
              {showTable ? <BarChart3 className="h-3.5 w-3.5" /> : <Table2 className="h-3.5 w-3.5" />}
              {showTable ? t('analytics.common.showChart') : t('analytics.common.showTable')}
            </button>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
}
